import { useMemo } from "react"
import { useFieldArray, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { useAssignRef } from "shared/lib/useAssignRef"

import { zodResolver } from "@hookform/resolvers/zod"
import { CreateTeamMemberInput } from "entities/team-members/types/CreateTeamMemberInput"
import { TeamRole } from "entities/team-members/types/TeamRole"
import { useTeamRoleOptions } from "entities/teams/model/useTeamRoleOptions"
import {
  CreateTeamMemberFormData,
  createSingleTeamMemberFormSchema
} from "features/team-member-management/types/CreateTeamMemberFormData"

import { CreateTeamMemberFormViewModel } from "../types/CreateTeamMemberFormView"
import { SingleTeamInvitationFormProps } from "../ui/CreateSingleTeamMemberForm"

const defaultInvite: CreateTeamMemberInput = {
  email: "",
  role: TeamRole.READ
}

const defaultInitialInvites: CreateTeamMemberInput[] = [defaultInvite]

export function useCreateTeamMemberFormViewModel({
  defaultValue,
  onSubmit,
  formRef,
  currentUserEmail,
  existingTeamMemberEmails,
  ...rest
}: SingleTeamInvitationFormProps): CreateTeamMemberFormViewModel {
  const roleOptions = useTeamRoleOptions()
  const { t } = useTranslation()

  // Create dynamic validation schema based on current user and existing members
  const validationSchema = useMemo(
    () =>
      createSingleTeamMemberFormSchema(
        currentUserEmail,
        existingTeamMemberEmails
      ),
    [currentUserEmail, existingTeamMemberEmails]
  )

  const form = useForm<CreateTeamMemberFormData>({
    defaultValues: defaultValue || { teamMembers: defaultInitialInvites },

    resolver: zodResolver<
      CreateTeamMemberFormData,
      unknown,
      CreateTeamMemberFormData
    >(validationSchema)
  })

  useAssignRef({ ref: formRef, value: form })

  const fieldArray = useFieldArray({
    control: form.control,
    name: "teamMembers"
  })

  return {
    ...rest,
    form,
    fieldArray,
    roleOptions,
    inviteMoreButtonText: t("Team.Member.InviteMultiple"),
    onSubmit: form.handleSubmit(onSubmit)
  }
}
